import React, { HTMLAttributes, FC } from 'react'
import cn from 'classnames';

type TableProps = {
  children?: React.ReactNode;
} & HTMLAttributes<HTMLTableElement>;

type TableSectionProps = {
  children?: React.ReactNode;
  className?: string;
}

type TableRowProps = {
  children?: React.ReactNode;
} & HTMLAttributes<HTMLTableRowElement>;

type TableCellProps = {
  children?: React.ReactNode;
  isHeader?: boolean;
  align?: 'left' | 'center' | 'right';
} & HTMLAttributes<HTMLTableCellElement>;

/**
 * Table component for rendering tabular data with customizable sections.
 *
 * @remarks
 * This component serves as a wrapper for a table element, providing overflow handling and full width styling.
 * It also exposes static subcomponents for defining the table's head, body, rows, and cells.
 *
 * @param children - The content of the table, typically composed of Table.Head, Table.Body, Table.Row, and Table.Cell components.
 * @param className - Additional CSS classes to apply to the table element.
 * @param props - Additional props spread onto the table element.
 *
 * @example
 * ```tsx
 * <Table>
 *   <Table.Head>...</Table.Head>
 *   <Table.Body>...</Table.Body>
 * </Table>
 * ```
 */
const Table: FC<TableProps> & {
  Head: FC<TableSectionProps>;
  Body: FC<TableSectionProps>;
  Row: FC<TableRowProps>;
  Cell: FC<TableCellProps>;
} = ({children, className, ...props}) => {
  return (
  <div className='overflow-auto overscroll-auto w-full h-full'>
     <table
      className={cn("", className)}
      {...props}
    >
      {children}
    </table>
  </div>
   
  )
}

Table.Head = ({children, className}: TableSectionProps) => <thead className={cn("bg-surface-100", className)}>{children}</thead>;

Table.Body = ({children}: TableSectionProps) => <tbody className="bg-surface-0">{children}</tbody>;

Table.Row = ({children, className, ...props}: TableRowProps) => (
  <tr
    className={cn("border-b border-surface-200 hover:bg-surface-50 w-full", className)}
    {...props}
  >
    {children}
  </tr>
)

Table.Cell = ({children, isHeader = false, align = 'left', className, ...props}: TableCellProps) => {
  const commonStyles = cn(
    "px-4 py-2 w-fit border-surface-200",
    {
      'text-left': align === 'left',
      'text-center': align === 'center',
      'text-right': align === 'right',
    },
    className
  );

  return isHeader ? (
    <th className={cn("font-texts font-semibold text-surface-800 bg-surface-100", commonStyles)} {...props}>
      {children}
    </th>
  ) : (
    <td className={cn("text-surface-700 font-normal font-texts bg-transparent", commonStyles)} {...props}>
      {children}
    </td>
  );
}

export default Table;